import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { CommonService } from '../../common-service';
import { ReturnStruct } from '../../structs/return.struct';
import {
  CamposOportunidadeRequest,
  ComentarioOportunidadeRequest,
  CriarOportunidadeRequest,
  EncerrarOportunidadeRequest,
  MoverOportunidadeRequest,
  EnviarEmailOportunidadeRequest,
  ProximaAcaoOportunidadeRequest,
  StatusClienteOportunidadeRequest,
  TagsOportunidadeRequest,
  UploadAnexoOportunidadeRequest,
} from '../../requests/vendas/oportunidade.request';
import { GetOportunidadesFiltroRequest } from '../../requests/vendas/get-oportunidades-filtro.request';
import { CriarOportunidadeOcorrenciaRequest } from '../../requests/vendas/criar-oportunidade-ocorrencia.request';
import { OportunidadeOcorrenciaEditarRequest } from '@shared/services/requests/vendas/oportunidade-ocorrencia-editar.request';
import {
  ComentarioOportunidadeResponse,
  CreateOportunidadeResponse,
  GetAnexosOportunidadeResponse,
  GetLookupsOportunidadeResponse,
  GetComentariosOportunidadeResponse,
  GetUrlAnexoOportunidadeResponse,
  ProximaAcaoOportunidadeResponse,
  TagsOportunidadeResponse,
  UploadAnexoOportunidadeResponse,
} from '../../responses/vendas/oportunidade.response';
import { GetOportunidadesClienteResponse } from '@shared/services/responses/vendas/get-oportunidades-cliente.response';
import { GetConfiguracoesOportunidadeResponse } from '@shared/services/responses/vendas/get-configuracoes-oportunidade.response';
import { GetOportunidadesKanbanComValorResponse } from '@shared/services/responses/vendas/get-oportunidades-kanban-com-valor.response';
import { GetOportunidadesEtapaComValorResponse } from '@shared/services/responses/vendas/get-oportunidades-etapa-com-valor.response';
import { GetOportunidadesListaComValorResponse } from '@shared/services/responses/vendas/get-oportunidades-lista-com-valor.response';
import { GetOportunidadeDetalheComValorResponse } from '@shared/services/responses/vendas/get-oportunidade-detalhe-com-valor.response';
import { GetDocumentosOportunidadeResponse } from '@shared/services/responses/vendas/get-documentos-oportunidade.response';
import { GetOrcamentosEmAbertoResponse } from '@shared/services/responses/vendas/get-orcamentos-em-aberto.response';
import { GetHistoricoClienteOportunidadeResponse } from '@shared/services/responses/vendas/get-historico-cliente-oportunidade.response';
import { GetFinanceiroOportunidadeResponse } from '@shared/services/responses/vendas/get-financeiro-oportunidade.response';
import { GetOportunidadeHistoricoComVendasResponse } from '@shared/services/responses/vendas/get-oportunidade-historico-com-vendas.response';
import { AtualizarOportunidadeOrquestradorResponse } from '@shared/services/responses/vendas/atualizar-oportunidade-orquestrador.response';
import { AtualizarOportunidadeOrquestradorRequest } from '@shared/services/requests/vendas/atualizar-oportunidade-orquestrador.request';

/**
 * Oportunidades do CRM — board, lista, ficha, anexos e comentários.
 *
 * Aponta para o ORQUESTRADOR, nunca para a urlApiCrm direto: é ele quem resolve o escopo de
 * visualização do vendedor e o injeta em cada chamada ao CRM. Só os lookups globais (origem,
 * canal, resultado, motivo) continuam direto no CRM, porque não dependem de escopo.
 */
@Injectable({
  providedIn: 'root',
})
export class OportunidadeService extends CommonService {
  private readonly apiUrl: string = environment.urlApiOrquestradorVendas + 'Oportunidade';
  private readonly anexoUrl: string = environment.urlApiOrquestradorVendas + 'Oportunidade/anexo';
  private readonly comentarioUrl: string = environment.urlApiOrquestradorVendas + 'Oportunidade/comentario';
  private readonly lookupsUrl: string = environment.urlApiCrm + 'Oportunidade/lookups';

  constructor(private httpClient: HttpClient) {
    super();
  }

  /** Monta os filtros da barra, omitindo o que não foi informado. */
  private montarParams(filtro?: GetOportunidadesFiltroRequest): HttpParams {
    let params = new HttpParams();
    if (!filtro) return params;

    if (filtro.pesquisa) params = params.set('pesquisa', filtro.pesquisa);
    if (filtro.chaveEtapa) params = params.set('chaveEtapa', filtro.chaveEtapa);
    if (filtro.idVendedorResponsavel != null)
      params = params.set('idVendedorResponsavel', filtro.idVendedorResponsavel.toString());
    if (filtro.idPessoaCliente != null)
      params = params.set('idPessoaCliente', filtro.idPessoaCliente.toString());
    if (filtro.idTipoPrioridade != null)
      params = params.set('idTipoPrioridade', filtro.idTipoPrioridade.toString());
    if (filtro.idOrigemLead != null)
      params = params.set('idOrigemLead', filtro.idOrigemLead.toString());
    if (filtro.valorMinimo != null) params = params.set('valorMinimo', filtro.valorMinimo.toString());
    if (filtro.valorMaximo != null) params = params.set('valorMaximo', filtro.valorMaximo.toString());
    if (filtro.isArquivado != null) params = params.set('isArquivado', filtro.isArquivado);
    if (filtro.comAcaoPendente != null) params = params.set('comAcaoPendente', filtro.comAcaoPendente);
    if (filtro.isEncerradas != null) params = params.set('isEncerradas', filtro.isEncerradas);
    if (filtro.isFavoritas != null) params = params.set('isFavoritas', filtro.isFavoritas);
    if (filtro.idUsuarioResponsavelAcao != null)
      params = params.set('idUsuarioResponsavelAcao', filtro.idUsuarioResponsavelAcao.toString());
    if (filtro.dataInicio) params = params.set('dataInicio', filtro.dataInicio);
    if (filtro.dataFim) params = params.set('dataFim', filtro.dataFim);
    if (filtro.filtroData != null) params = params.set('filtroData', filtro.filtroData.toString());

    return params;
  }

  /** Lookups globais — uma chamada no carregamento da tela. */
  public getLookups(): Observable<GetLookupsOportunidadeResponse> {
    return this.httpClient
      .get<GetLookupsOportunidadeResponse>(this.lookupsUrl, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /**
   * Escopo de visualização do usuário logado, vendedores da empresa e "Restringir acesso dos
   * vendedores" — uma chamada no carregamento da tela. Sem ela o board não carrega (CA-13).
   */
  public getConfiguracoes(): Observable<GetConfiguracoesOportunidadeResponse> {
    return this.httpClient
      .get<GetConfiguracoesOportunidadeResponse>(`${this.apiUrl}/configuracoes`, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  // --- Board ---

  public getKanban(filtro?: GetOportunidadesFiltroRequest, tamanhoPagina = 50): Observable<GetOportunidadesKanbanComValorResponse> {
    const params = this.montarParams(filtro).set('tamanhoPagina', tamanhoPagina.toString());
    return this.httpClient
      .get<GetOportunidadesKanbanComValorResponse>(`${this.apiUrl}/kanban`, { params, ...this.addHeaderToken() })
      .pipe(catchError(this.handleError));
  }

  public getKanbanEtapa(filtro: GetOportunidadesFiltroRequest, pagina = 0,
    tamanhoPagina = 50): Observable<GetOportunidadesEtapaComValorResponse> {
    const params = this.montarParams(filtro)
      .set('pagina', pagina.toString())
      .set('tamanhoPagina', tamanhoPagina.toString());
    return this.httpClient
      .get<GetOportunidadesEtapaComValorResponse>(`${this.apiUrl}/kanban/etapa`, { params, ...this.addHeaderToken() })
      .pipe(catchError(this.handleError));
  }

  public getLista(filtro?: GetOportunidadesFiltroRequest, pagina = 0,
    tamanhoPagina = 200): Observable<GetOportunidadesListaComValorResponse> {
    const params = this.montarParams(filtro)
      .set('pagina', pagina.toString())
      .set('tamanhoPagina', tamanhoPagina.toString());
    return this.httpClient
      .get<GetOportunidadesListaComValorResponse>(`${this.apiUrl}/lista`, { params, ...this.addHeaderToken() })
      .pipe(catchError(this.handleError));
  }

  public getById(idOportunidade: number): Observable<GetOportunidadeDetalheComValorResponse> {
    return this.httpClient
      .get<GetOportunidadeDetalheComValorResponse>(`${this.apiUrl}/${idOportunidade}`, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /** Documentos de Vendas vinculados (aba Vendas da ficha). Exige Visualizar do Processo de Venda. */
  public getDocumentos(idOportunidade: number): Observable<GetDocumentosOportunidadeResponse> {
    return this.httpClient
      .get<GetDocumentosOportunidadeResponse>(`${this.apiUrl}/idOportunidade/${idOportunidade}/documentos`, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /**
   * Orçamentos em aberto vinculados: o que a troca de vendedor pergunta se leva junto. Conta só os do escopo
   * do usuário e sem os que já são do vendedor de destino.
   */
  public getOrcamentosEmAberto(idOportunidade: number, idVendedorResponsavel: number): Observable<GetOrcamentosEmAbertoResponse> {
    return this.httpClient
      .get<GetOrcamentosEmAbertoResponse>(
        `${this.apiUrl}/idOportunidade/${idOportunidade}/orcamentos-em-aberto?idVendedorResponsavel=${idVendedorResponsavel}`,
        this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /**
   * Histórico do cliente na aba Vendas: últimos pedidos, últimas notas fiscais e outros documentos do cliente sem
   * oportunidade. Exige Visualizar do CRM e do Processo de Venda.
   */
  public getHistoricoCliente(idOportunidade: number): Observable<GetHistoricoClienteOportunidadeResponse> {
    return this.httpClient
      .get<GetHistoricoClienteOportunidadeResponse>(
        `${this.apiUrl}/idOportunidade/${idOportunidade}/historico-cliente`, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /**
   * Aba Financeiro da ficha, só para consulta: títulos a receber do cliente (com os pagos recentes quando pedido),
   * limite de crédito e parcelas dos pedidos vinculados. Os totais não mudam com `incluirPagosRecentes`.
   */
  public getFinanceiro(idOportunidade: number, incluirPagosRecentes: boolean): Observable<GetFinanceiroOportunidadeResponse> {
    const params = new HttpParams().set('incluirPagosRecentes', incluirPagosRecentes.toString());
    return this.httpClient
      .get<GetFinanceiroOportunidadeResponse>(
        `${this.apiUrl}/idOportunidade/${idOportunidade}/financeiro`, { params, ...this.addHeaderToken() })
      .pipe(catchError(this.handleError));
  }

  /**
   * Ocorrências do histórico do CRM e, com Visualizar do Processo de Venda, as dos documentos de Vendas vinculados;
   * mais recentes primeiro (comentário não entra no histórico).
   */
  public getHistorico(idOportunidade: number): Observable<GetOportunidadeHistoricoComVendasResponse> {
    return this.httpClient
      .get<GetOportunidadeHistoricoComVendasResponse>(
        `${this.apiUrl}/idOportunidade/${idOportunidade}/historico`, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /** Anotação do usuário no histórico (ocorrência tipo 5). */
  public criarOcorrencia(idOportunidade: number,
    request: CriarOportunidadeOcorrenciaRequest): Observable<ReturnStruct> {
    return this.httpClient
      .post<ReturnStruct>(
        `${this.apiUrl}/idOportunidade/${idOportunidade}/ocorrencia`, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /** Nova descrição de um registro da linha do tempo (anotação ou ação concluída). */
  public atualizarOcorrencia(idOportunidade: number, idOportunidadeOcorrencia: number,
    request: OportunidadeOcorrenciaEditarRequest): Observable<ReturnStruct> {
    return this.httpClient
      .put<ReturnStruct>(
        `${this.apiUrl}/idOportunidade/${idOportunidade}/ocorrencia/${idOportunidadeOcorrencia}`, request,
        this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /** Exclui um registro da linha do tempo; o de ação pendente leva junto a ação ainda não concluída. */
  public excluirOcorrencia(idOportunidade: number, idOportunidadeOcorrencia: number): Observable<ReturnStruct> {
    return this.httpClient
      .delete<ReturnStruct>(
        `${this.apiUrl}/idOportunidade/${idOportunidade}/ocorrencia/${idOportunidadeOcorrencia}`,
        this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /** Últimas oportunidades da pessoa no escopo do usuário, da mais recente para a mais antiga. */
  public getUltimasDoCliente(idPessoaCliente: number, quantidade = 10): Observable<GetOportunidadesClienteResponse> {
    const params = new HttpParams().set('quantidade', quantidade.toString());
    return this.httpClient
      .get<GetOportunidadesClienteResponse>(
        `${this.apiUrl}/cliente/idPessoa/${idPessoaCliente}/ultimas`, { params, ...this.addHeaderToken() })
      .pipe(catchError(this.handleError));
  }

  // --- Card ---

  public create(request: CriarOportunidadeRequest): Observable<CreateOportunidadeResponse> {
    return this.httpClient
      .post<CreateOportunidadeResponse>(this.apiUrl, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  public update(idOportunidade: number,
    request: AtualizarOportunidadeOrquestradorRequest): Observable<AtualizarOportunidadeOrquestradorResponse> {
    return this.httpClient
      .put<AtualizarOportunidadeOrquestradorResponse>(`${this.apiUrl}/${idOportunidade}`, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  public mover(idOportunidade: number, request: MoverOportunidadeRequest): Observable<CreateOportunidadeResponse> {
    return this.httpClient
      .put<CreateOportunidadeResponse>(`${this.apiUrl}/${idOportunidade}/etapa`, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  public encerrar(idOportunidade: number,
    request: EncerrarOportunidadeRequest): Observable<CreateOportunidadeResponse> {
    return this.httpClient
      .put<CreateOportunidadeResponse>(`${this.apiUrl}/${idOportunidade}/encerrar`, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  public reabrir(idOportunidade: number): Observable<CreateOportunidadeResponse> {
    return this.httpClient
      .put<CreateOportunidadeResponse>(`${this.apiUrl}/${idOportunidade}/reabrir`, {}, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  public arquivar(idOportunidade: number, arquivar: boolean): Observable<ReturnStruct> {
    const params = new HttpParams().set('arquivar', arquivar);
    return this.httpClient
      .put<ReturnStruct>(`${this.apiUrl}/${idOportunidade}/arquivar`, {}, { params, ...this.addHeaderToken() })
      .pipe(catchError(this.handleError));
  }

  /** Marca ou desmarca a oportunidade como favorita do usuário logado. */
  public favoritar(idOportunidade: number, favoritar: boolean): Observable<ReturnStruct> {
    const params = new HttpParams().set('favoritar', favoritar);
    return this.httpClient
      .put<ReturnStruct>(`${this.apiUrl}/idOportunidade/${idOportunidade}/favorito`, {}, { params, ...this.addHeaderToken() })
      .pipe(catchError(this.handleError));
  }

  public delete(idOportunidade: number): Observable<ReturnStruct> {
    return this.httpClient
      .delete<ReturnStruct>(`${this.apiUrl}/${idOportunidade}`, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  public salvarCampos(idOportunidade: number, request: CamposOportunidadeRequest): Observable<ReturnStruct> {
    return this.httpClient
      .put<ReturnStruct>(`${this.apiUrl}/${idOportunidade}/campos`, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  public salvarTags(idOportunidade: number, request: TagsOportunidadeRequest): Observable<TagsOportunidadeResponse> {
    return this.httpClient
      .put<TagsOportunidadeResponse>(`${this.apiUrl}/${idOportunidade}/tags`, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  public salvarProximaAcao(idOportunidade: number,
    request: ProximaAcaoOportunidadeRequest): Observable<ProximaAcaoOportunidadeResponse> {
    return this.httpClient
      .put<ProximaAcaoOportunidadeResponse>(
        `${this.apiUrl}/${idOportunidade}/proxima-acao`, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  public concluirProximaAcao(idOportunidade: number): Observable<ReturnStruct> {
    return this.httpClient
      .put<ReturnStruct>(`${this.apiUrl}/${idOportunidade}/proxima-acao/concluir`, {}, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /** Envia o e-mail do painel "Mensagem CRM" e registra a ocorrência no histórico. */
  public enviarEmail(idOportunidade: number,
    request: EnviarEmailOportunidadeRequest): Observable<ReturnStruct> {
    return this.httpClient
      .post<ReturnStruct>(`${this.apiUrl}/${idOportunidade}/email`, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /**
   * Só registra a ocorrência no histórico (CA09): chamar depois que PessoaService.atualizarStatusCrm
   * confirmar a troca. A API espalha para todos os cards do cliente, não só o clicado.
   */
  public registrarStatusCliente(idOportunidade: number,
    request: StatusClienteOportunidadeRequest): Observable<ReturnStruct> {
    return this.httpClient
      .post<ReturnStruct>(
        `${this.apiUrl}/idOportunidade/${idOportunidade}/status-cliente`, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  // --- Anexos ---

  public getAnexos(idOportunidade: number): Observable<GetAnexosOportunidadeResponse> {
    return this.httpClient
      .get<GetAnexosOportunidadeResponse>(`${this.anexoUrl}/${idOportunidade}`, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  public uploadAnexo(idOportunidade: number,
    request: UploadAnexoOportunidadeRequest): Observable<UploadAnexoOportunidadeResponse> {
    return this.httpClient
      .post<UploadAnexoOportunidadeResponse>(`${this.anexoUrl}/${idOportunidade}`, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /** URL assinada de 60 min — não guardar, pedir de novo a cada download. */
  public getUrlAnexo(idOportunidade: number, idAnexo: number): Observable<GetUrlAnexoOportunidadeResponse> {
    return this.httpClient
      .get<GetUrlAnexoOportunidadeResponse>(
        `${this.anexoUrl}/${idOportunidade}/${idAnexo}/url`, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  public deleteAnexo(idOportunidade: number, idAnexo: number): Observable<ReturnStruct> {
    return this.httpClient
      .delete<ReturnStruct>(`${this.anexoUrl}/${idOportunidade}/${idAnexo}`, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  // --- Comentários ---

  public getComentarios(idOportunidade: number): Observable<GetComentariosOportunidadeResponse> {
    return this.httpClient
      .get<GetComentariosOportunidadeResponse>(`${this.comentarioUrl}/${idOportunidade}`, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  public adicionarComentario(idOportunidade: number,
    request: ComentarioOportunidadeRequest): Observable<ComentarioOportunidadeResponse> {
    return this.httpClient
      .post<ComentarioOportunidadeResponse>(`${this.comentarioUrl}/${idOportunidade}`, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  public atualizarComentario(idOportunidade: number, idComentario: number,
    request: ComentarioOportunidadeRequest): Observable<ComentarioOportunidadeResponse> {
    return this.httpClient
      .put<ComentarioOportunidadeResponse>(
        `${this.comentarioUrl}/${idOportunidade}/${idComentario}`, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  public deleteComentario(idOportunidade: number, idComentario: number): Observable<ReturnStruct> {
    return this.httpClient
      .delete<ReturnStruct>(`${this.comentarioUrl}/${idOportunidade}/${idComentario}`, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }
}
